import {
    captureWalletEvent,
    eventError,
    eventId,
    type EventOwner,
    type MigrationStep,
    ownerForAddress,
    type TransactionStage,
    type WalletEvent,
    type WalletEventProperties
} from "@shared/analytics/walletEvents";
import { walletJourney } from "@shared/analytics/walletJourney";
import type { TransactionObserver } from "@shared/types/transactionObserver";
import { CacheDuration } from "@solomon-labs/caching";
import { ConnectionManager } from "@solomon-labs/solana-connection";
import type { SolanaTransactionSignature } from "@solomon-labs/types";

export interface MigrationAttemptRecord {
    signature?: string;
    origin?: "imported";
    journeyId: string;
    attemptId: string;
    step: MigrationStep;
    number: number;
}
interface JourneyRecord {
    id: string;
    count: number;
    completed: boolean;
    lastAttempt?: MigrationAttemptRecord;
    attempts?: Record<string, MigrationAttemptRecord>;
    outcomes: Partial<Record<string, "succeeded" | "failed" | "deferred">>;
}
const memory = new Map<string, JourneyRecord>();
const key = (address: string): string => `solomon:analytics:migration:${address}`;
function read(address: string): JourneyRecord | undefined {
    if (memory.has(address)) return memory.get(address);
    try {
        const raw: unknown = JSON.parse(localStorage.getItem(key(address)) ?? "null");
        if (
            raw &&
            typeof raw === "object" &&
            "id" in raw &&
            typeof raw.id === "string" &&
            "count" in raw &&
            typeof raw.count === "number" &&
            "completed" in raw &&
            typeof raw.completed === "boolean" &&
            "outcomes" in raw &&
            raw.outcomes &&
            typeof raw.outcomes === "object"
        ) {
            const value = raw as JourneyRecord;
            memory.set(address, value);
            return value;
        }
    } catch {
        /* Storage is optional for analytics. */
    }
    return undefined;
}
function save(address: string, value: JourneyRecord): void {
    memory.set(address, value);
    try {
        localStorage.setItem(key(address), JSON.stringify(value));
    } catch {
        /* Keep this page's IDs. */
    }
}
export function migrationContext(address: string | null): WalletEventProperties {
    const journey = address ? read(address) : undefined;
    return { entry_point: "wallet_menu", migration_type: "legacy_usdv", migration_journey_id: journey?.id };
}
export function openMigrationJourney(address: string): WalletEventProperties {
    const previous = read(address);
    const journey =
        previous && !previous.completed ? previous : { id: eventId(), count: 0, completed: false, outcomes: {} };
    save(address, journey);
    return {
        entry_point: "wallet_menu",
        migration_type: "legacy_usdv",
        migration_journey_id: journey.id,
        continuity: journey === previous ? "resumed" : "new_context"
    };
}
export function completeMigrationJourney(address: string, properties: WalletEventProperties): void {
    const journey = read(address);
    if (
        !journey ||
        journey.completed ||
        !Object.values(journey.outcomes).includes("succeeded") ||
        Object.values(journey.outcomes).includes("deferred")
    )
        return;
    journey.completed = true;
    save(address, journey);
    captureWalletEvent(
        "migration:completed",
        { ...migrationContext(address), ...properties },
        ownerForAddress(address, walletJourney.owner().properties)
    );
}

export class MigrationAttempt {
    readonly record: MigrationAttemptRecord;
    readonly owner: EventOwner;
    private stage: TransactionStage = "building";
    private readonly started = performance.now();
    private observed = false;
    private readonly imported: boolean;
    private readonly retryOf?: string;
    constructor(
        private readonly address: string,
        step: MigrationStep,
        restored?: MigrationAttemptRecord,
        private readonly menuOpen: () => boolean = () => false,
        private readonly emit = captureWalletEvent
    ) {
        openMigrationJourney(address);
        const journey = read(address)!;
        this.imported = !!restored;
        if (restored) journey.id = restored.journeyId;
        this.record = restored ?? { journeyId: journey.id, attemptId: eventId(), step, number: ++journey.count };
        this.owner = ownerForAddress(address, { ...walletJourney.owner().properties });
        if (!restored) {
            const previous = journey.lastAttempt;
            this.retryOf =
                previous?.step === step && journey.outcomes[previous.attemptId] === "failed"
                    ? previous.attemptId
                    : undefined;
            this.send("migration:step_requested", { retry_of_attempt_id: this.retryOf });
            journey.lastAttempt = this.record;
        }
        journey.attempts = { ...journey.attempts, [this.record.attemptId]: this.record };
        save(address, journey);
    }
    properties(): WalletEventProperties {
        return {
            entry_point: "wallet_menu",
            migration_type: "legacy_usdv",
            migration_journey_id: this.record.journeyId,
            migration_attempt_id: this.record.attemptId,
            step: this.record.step,
            attempt_number: this.record.number,
            attempt_origin: this.record.origin
        };
    }
    private send(event: WalletEvent, properties: WalletEventProperties = {}): void {
        this.emit(event, { ...this.properties(), ...properties }, this.owner);
    }
    start(): void {
        this.observed = true;
        this.send("migration:transaction_started", { retry_of_attempt_id: this.retryOf });
    }
    block(reason: string): void {
        this.send("migration:blocked", { reason });
    }
    observer: TransactionObserver = {
        stage: (stage): void => {
            if (stage === this.stage && stage !== "building") return;
            this.stage = stage;
            this.send("migration:transaction_stage_changed", { stage });
        },
        submitted: (signature): void => {
            this.record.signature = signature;
            save(this.address, read(this.address)!);
            this.send("migration:transaction_submitted");
        },
        failed: (error, uncertain): void => {
            this.finish(uncertain ? "deferred" : "failed", eventError(error));
        }
    };
    finish(
        outcome: "succeeded" | "failed" | "deferred",
        properties: WalletEventProperties = {},
        source: "live" | "recovery" = "live"
    ): void {
        const journey = read(this.address)!;
        const previous = journey.outcomes[this.record.attemptId];
        if (
            previous === "succeeded" ||
            previous === "failed" ||
            previous === outcome ||
            (previous === "deferred" && source === "live")
        )
            return;
        journey.outcomes[this.record.attemptId] = outcome;
        save(this.address, journey);
        const event =
            outcome === "deferred"
                ? "migration:transaction_confirmation_deferred"
                : outcome === "succeeded"
                  ? "migration:transaction_completed"
                  : "migration:transaction_failed";
        this.send(event, {
            ...(outcome === "deferred"
                ? { uncertain_stage: this.stage, reason: "receipt_unavailable" }
                : outcome === "failed"
                  ? { failure_stage: this.stage }
                  : {}),
            ...properties,
            menu_open: this.menuOpen(),
            result_source: source,
            duration_ms: this.observed && !this.imported ? performance.now() - this.started : undefined
        });
    }
}

const checking = new Set<string>();
/** Resolve observed staking submissions without sending their signatures to analytics. */
export async function reconcileMigrationTransactions(address: string, menuOpen: boolean): Promise<boolean> {
    if (checking.has(address)) return false;
    checking.add(address);
    let changed = false;
    try {
        const journey = read(address);
        for (const record of Object.values(journey?.attempts ?? {})) {
            if (
                !record ||
                journey?.outcomes[record.attemptId] !== "deferred" ||
                !record.signature ||
                !/^[1-9A-HJ-NP-Za-km-z]{64,100}$/.test(record.signature) ||
                (record.step !== "unstake_legacy_susdv" && record.step !== "claim_legacy_usdv")
            )
                continue;
            const attempt = new MigrationAttempt(address, record.step, record, () => menuOpen);
            const properties = {
                ...attempt.properties(),
                check_id: eventId(),
                check_type: "receipt" as const,
                trigger: "resume"
            };
            captureWalletEvent("migration:check_started", properties, attempt.owner);
            try {
                const transaction = await ConnectionManager.getTransaction(
                    record.signature as SolanaTransactionSignature,
                    { commitment: "confirmed", maxSupportedTransactionVersion: 0 },
                    { cacheDuration: CacheDuration.Bust }
                );
                const result = !transaction?.meta ? "pending" : transaction.meta.err ? "failed" : "confirmed";
                captureWalletEvent("migration:check_completed", { ...properties, result }, attempt.owner);
                if (result !== "pending") {
                    attempt.finish(result === "confirmed" ? "succeeded" : "failed", {}, "recovery");
                    changed = true;
                }
            } catch (error) {
                captureWalletEvent("migration:check_failed", { ...properties, ...eventError(error) }, attempt.owner);
            }
        }
    } finally {
        checking.delete(address);
    }
    return changed;
}
